import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  buildMiiPrompts,
  buildMiiStaticJobDescription,
  buildMiiEmailResponse,
  buildMiiLinks,
} from "../defaults";
import { useAuth } from "../../auth/AutoContextProvider";
import { useCommon } from "../../common/CommonServiceProvider";
import { apiErrorCodes } from "../../common/components/defaults";
import { Loader } from "../../common/components/Loader";
import PreformattedTextContainer from "../../common/components/PreformattedTextContainer";
import ConfirmationModal from "../../common/modals/confirmationModal";
import { parseEOJD } from "../../util/string-helper";
import BuildMiiEmailModal from "../../common/modals/buildMiiEmailModal";
import BuildMiiEmailSentModal from "../../common/modals/buildMiiEmailSentModal";
import BuildMiiWarningModal from "../../common/modals/buildMiiWarningModal";
import BuildMiiRefetchModal from "../../common/modals/buildMiiRefetchModal";
import BuildMiiJdIconHolder from "./BuildMiiJdIconHolder";
import arrowIconLeftBlue from "../../assets/images/common/arrow-icon-left-blue.png";
import arrowIconRightWhite from "../../assets/images/common/arrow-icon-right-white.png";
import salaryTable from "../../assets/images/common/salary-table-v2.png";
import { useBuildMii } from "../BuildMiiServiceProvider";
import "../../assets/css/components/buildMiiJd.scss";

const BuildMiiJd = (props) => {
  const [
    {
      getBuildMiiOneShotJobDescriptionRequest,
      getBuildMiiTypeFormResponseRequest,
      getBuildMiiTypeFormConfigurationRequest,
    },
  ] = useBuildMii();
  const [_, { sso, getCookie, getFinalGuestToken, getInitialGuestToken }] = useAuth();
  const [{ refreshLimitToken }] = useCommon();
  const [searchParams] = useSearchParams();
  const uId = searchParams.get("u_uid");
  const [jobDescription, setJobDescription] = useState("");
  const [jobDescriptionStatus, setJobDescriptionStatus] = useState("");
  const [error] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [emailSentObject, setEmailSentResponseObject] = useState({
    emailStatus: "",
    emailTitle: "",
    emailMessage: "",
    showEmailModal: false,
  });
  const [warningObject, setWarningResponseObject] = useState({
    warningSttus: "",
    warningTitle: "",
    warningMessage: "",
    showWarningModal: false,
  });
  const [loading, setLoading] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isBuildMiiEmailModalOpen, setIsBuildMiiEmailModalOpen] = useState(false);
  const [isBuildMiiRefetchModalOpen, setIsBuildMiiRefetchModalOpen] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [showInterviewQuestions, setShowInterviewQuestions] = useState(false);
  const [showPersonality, setShowPersonality] = useState(false);
  const [typeFormFieldObject, setTypeFormFieldObject] = useState([]);
  const [finalTypeFormObject, setFinalTypeFormObject] = useState([]);
  const refresh = getCookie("tkn-ath-rfrsh");

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "PrintScreen") {
        setWarningResponseObject({
          warningStatus: "warning",
          warningTitle: "Warning!",
          warningMessage: "Taking screenshots is not allowed on this page.",
          showWarningModal: true,
        });
        event.preventDefault();
      }
    };
    document.addEventListener("keyup", handleKeyPress);
    return () => {
      document.removeEventListener("keyup", handleKeyPress);
    };
  }, []);

  useEffect(() => {
    const uId = searchParams.get("u_uid");
    const formId = searchParams.get("form_id");
    if (uId && formId) {
      setLoading(true);
    }

    const fetchAll = () => {
      if (!refresh) {
        refreshAuthentication();
      }

      if (formId) {
        refreshLimitToken(refresh);
        getTypeFormConfigurationRequest(formId)
          .then((data) => {
            if (Array.isArray(data)) {
              refreshLimitToken(refresh).then(() => {
                getTypeFormRequest(uId, data)
                  .then((data) => {
                    const prompt = buildMiiPrompts.entryPrompt;
                    const answerObject = data || finalTypeFormObject;
                    if (answerObject) {
                      getJobDescriptionRequest(prompt, answerObject)
                    }
                  })
                  .catch((error) => {
                    refreshLimitToken(refresh);
                    setJobDescription(error);
                    setJobDescriptionStatus("fail");
                    setLoading(false);
                    setIsBuildMiiRefetchModalOpen(true)
                  });
              });
            }
          })
          .catch((error) => {
            refreshLimitToken(refresh);
            setJobDescription(error);
            setJobDescriptionStatus("fail");
            setLoading(false);
            setIsBuildMiiRefetchModalOpen(true)
          });
      }
    };
    fetchAll();
  }, []);

  const refreshAuthentication = async () => {
    await getInitialGuestToken().then((url) => {
      getFinalGuestToken(url.refresh_token).then((data) => {
        sso(data.token, data.refresh_token, data.role);
      });
    });
  };

  const handleRefetchJD = async() => {
    setIsBuildMiiRefetchModalOpen(false)
    const uId = searchParams.get("u_uid");
    const formId = searchParams.get("form_id");
    if (uId && formId) {
      setLoading(true);
    }

    if (!refresh) {
      refreshAuthentication();
    }

    if (formId) {
      refreshLimitToken(refresh);
      await getTypeFormConfigurationRequest(formId)
        .then((data) => {
          if (Array.isArray(data)) {
            refreshLimitToken(refresh).then(() => {
              getTypeFormRequest(uId, data)
                .then((data) => {
                  const prompt = buildMiiPrompts.entryPrompt;
                  const answerObject = data || finalTypeFormObject;
                  if (answerObject) {
                    getJobDescriptionRequest(prompt, answerObject);
                  }
                })
                .catch((error) => {
                  refreshLimitToken(refresh);
                  setJobDescription(error);
                  setJobDescriptionStatus("fail");
                  setLoading(false);
                  setIsBuildMiiRefetchModalOpen(true)
                });
            });
          }
        })
        .catch((error) => {
          refreshLimitToken(refresh);
          setJobDescription(error);
          setJobDescriptionStatus("fail");
          setLoading(false);
          setIsBuildMiiRefetchModalOpen(true)
        });
    }
  };

  const getTypeFormConfigurationRequest = useCallback(async (formId) => {
    try {
      refreshLimitToken(refresh);
      const response = await getBuildMiiTypeFormConfigurationRequest(formId);

      if (response?.status === 200) {
        const getFields = response.data.fields.map((field, index) => {
          const fieldObj = {
            index: index,
            id: field.id,
            title: field.title,
            type: field.type,
          };
          return fieldObj;
        });

        const filteredFields = getFields.filter(
          (field) => field.type !== "statement"
        );

        if (
          response?.headers["rate-limit-token"] &&
          response?.headers["rate-limit-refresh-token"]
        ) {
          sso(
            response?.headers["rate-limit-token"],
            response?.headers["rate-limit-refresh-token"]
          );
        }

        return filteredFields;
      } else {
        refreshLimitToken(refresh);
        setJobDescription(error);
        setJobDescriptionStatus("fail");
        setLoading(false);
        setIsBuildMiiRefetchModalOpen(true)
      }
    } catch (error) {
      refreshLimitToken(refresh);
      setJobDescription(error);
      setJobDescriptionStatus("fail");
      setLoading(false);
      setIsBuildMiiRefetchModalOpen(true)
    }
  }, []);

  const getTypeFormRequest = useCallback(async (uuid, typeFormFieldObject) => {
    refreshLimitToken(refresh);
    try {
      const typeFormQuestionObject = typeFormFieldObject;
      const response = await getBuildMiiTypeFormResponseRequest(uuid);
      if (response?.status === 200) {
        const answers = response?.data?.response?.answers;
        const newArray = typeFormQuestionObject.map((question) => {
          const { id, index } = question;
          const { value = "" } =
            answers.find(({ questionId }) => questionId === id) || {};
          return { index, id, value };
        });

        if (newArray.length) {
          setJobTitle(newArray[1].value);
          setFinalTypeFormObject([...newArray]);
        }

        if (
          response?.headers["rate-limit-token"] &&
          response?.headers["rate-limit-refresh-token"]
        ) {
          sso(
            response?.headers["rate-limit-token"],
            response?.headers["rate-limit-refresh-token"]
          );
        }

        return newArray;
      }else {
        setIsBuildMiiRefetchModalOpen(true)
      }
    } catch (error) {
      refreshLimitToken(refresh);
      setJobDescription(error);
      setJobDescriptionStatus("fail");
      setLoading(false);
      setIsBuildMiiRefetchModalOpen(true)
    }
  }, []);

  const getJobDescriptionRequest = useCallback(async (prompt, answers) => {
    refreshLimitToken(refresh);
    try {
      const response = await getBuildMiiOneShotJobDescriptionRequest(
        prompt,
        answers
      );
      if (!response.error) {
        const parsedResponse = await parseEOJD(response?.data?.description);

        setJobDescriptionStatus("success");
        setLoading(false);
        setJobDescription(parsedResponse);
        setIsBuildMiiRefetchModalOpen(false);

        if (
          response?.headers["rate-limit-token"] &&
          response?.headers["rate-limit-refresh-token"]
        ) {
          sso(
            response?.headers["rate-limit-token"],
            response?.headers["rate-limit-refresh-token"]
          );
        }
      }else {
        setIsBuildMiiRefetchModalOpen(true)
      }
    } catch (error) {
      console.log("error", error);
      const errorCode = error?.data?.error?.code;
      const errorDetails = apiErrorCodes.find(
        (errorCodeObj) => errorCodeObj.code === errorCode
      );
      if (errorDetails) {
        const errorMessage = `Error Overview: ${errorDetails.overview}\nCause: ${errorDetails.cause}\nSolution: ${errorDetails.solution}`;
        setJobDescription(errorMessage);
        setJobDescriptionStatus("fail");
      } else {
        setJobDescription(error);
        setJobDescriptionStatus("fail");
      }
      setLoading(false);
      refreshLimitToken(refresh);
      setIsBuildMiiRefetchModalOpen(true);
    }
  }, []);

  if (!jobDescription && error) {
    if (error) {
      setJobDescription("Server is busy. Please try again.");
      setJobDescriptionStatus("fail");
      setIsBuildMiiRefetchModalOpen(true)
    }
  }

  const handleBuildMiiEmailModalClick = (value) => {
    setIsBuildMiiEmailModalOpen(!value);
  };

  const handleConfirmationModalClick = (value) => {
    setIsConfirmationModalOpen(value);
  };

  const handleBuildMiiEmailSentModalClick = (value) => {
    setEmailSentResponseObject((prevEmailSentObject) => ({
      ...prevEmailSentObject,
      showEmailModal: !value,
    }));
  };

  const setEmailStatus = (newEmailStatus) => {
    setEmailSentResponseObject((prevEmailSentObject) => ({
      ...prevEmailSentObject,
      emailStatus: newEmailStatus,
      emailTitle: buildMiiEmailResponse[newEmailStatus].title,
      emailMessage: buildMiiEmailResponse[newEmailStatus].message,
      showEmailModal: true,
    }));
  };

  const handleWarningModalClose = () => {
    setWarningResponseObject((prevWarningObject) => ({
      ...prevWarningObject,
      showWarningModal: false,
    }));
  };

  return (
    <>
      <div className="flex md:justify-center md:flex-col flex-col bg-white-900">
        <div className="flex md:flex-row flex-col min-h-[60vh] justify-center md:w-3/4 w-full">
          <div className="flex flex-col h-full w-[30%]"></div>
          <div className="md:w-3/4 w-full ">
            <div className="px-3">
              <div
                className=" pb-1 inline-flex items-baseline cursor-pointer mt-10"
                onClick={(e) =>
                  handleConfirmationModalClick(!isConfirmationModalOpen)
                }
              >
                <img
                  src={arrowIconLeftBlue}
                  className="self-center w-5 h-5 rounded-full mx-1"
                  alt="Arrow Icon"
                />
                <span className="md:text-2xl text-lg font-bold">
                  Build a New Job Description
                </span>
              </div>
              <div className="flex flex-col h-full w-full p-[1rem] "></div>
              {jobDescription && (
                <PreformattedTextContainer
                  height={"h-[100%]"}
                  props={jobDescription}
                />
              )}
            </div>
          </div>

          <div
            id="download-jd"
            className="md:w-1/4 w-full mt-10 md:text-left text-center "
          >
            <div className="flex flex-col bg-blue-100 md:rounded-2xl shadow-lg">
              <div className="  text-slate-800 text-2xl font-bold justify-center text-center p-5 px-10">
                Download your finished Job Description
              </div>
              <div className="text-slate-800 text-[10px] justify-center text-center ">
                Click the <b>Send to Email</b> button to get a copy
              </div>
              <div className="mt-5 mb-10 w-full text-center">
                <button
                  className="w-[15rem] h-[4rem] bg-indigo-500 rounded-[6rem] justify-center items-start gap-2 inline-flex "
                  onClick={(e) =>
                    handleBuildMiiEmailModalClick(isBuildMiiEmailModalOpen)
                  }
                >
                  <div className="text-white text-lg font-bold leading-normal self-center text-white-900">
                    Send to Email
                  </div>
                  <img
                    src={arrowIconRightWhite}
                    className="self-center w-5 h-5 rounded-full mx-1 arrow-icon"
                    alt="Arrow Icon"
                  />
                </button>
              </div>
            </div>
            <div
              className=" pb-[2.15rem] inline-flex items-baseline cursor-pointer md:hidden visible mt-10"
              onClick={(e) =>
                handleConfirmationModalClick(!isConfirmationModalOpen)
              }
            >
              <img
                src={arrowIconLeftBlue}
                className="self-center w-5 h-5 rounded-full mx-1"
                alt="Arrow Icon"
              />
              <span className="md:text-2xl text-lg font-bold">
                Build a New Job Description
              </span>
            </div>
          </div>
        </div>
      </div>
      {/*}
      <div className="flex min-h-[60vh] bg-gray-1100">
        <div className="flex flex-col h-full md:w-[20%] w-full"></div>
        <div className="flex flex-col h-full w-full">
          <div div className="flex flex-row h-full w-full"></div>
          <div className="flex flex-col h-full w-full">
            <div className="flex flex-row">
              <span className="text-yellow-400 md:text-2xl text-xl font-bold leading-10 p-1">
                NEXT STEP:
              </span>
            </div>
            <div className="flex flex-row">
              <span className="text-white-900 md:text-left text-center md:text-4xl text-2xl font-bold leading-10 p-1">
                Explore The Average Salary Of Your Preferred Candidate
              </span>
            </div>
          </div>
          <div className=" flex flex-col h-full w-full">
            <div className="flex flex-row w-full">
              <span className="text-white-900 md:text-left text-center text-lg font-normal leading-loose p-2">
                You have the Job Description. Now it's time to explore our
                comprehensive salary guide to start hiring your ideal candidate!
              </span>
            </div>
            <div className="flex flex-row">
              <a
                href={buildMiiLinks.salaryGuide}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-500"
              >
                <button
                  className={
                    "w-[20rem] h-[4rem] bg-gradient-to-r from-themeColor-200 to-themeColor-100 text-white-900 text-2xl font-bold leading-10  rounded-full hover:bg-gray-300"
                  }
                >
                  Download the Guide
                </button>
              </a>
            </div>
          </div>
          <div className="flex flex-row h-full w-full"></div>
        </div>
        <div className="flex flex-col h-full w-[40%]">
          <div className="flex flex-row mt-auto">
            <img
              className="w-full h-full"
              src={salaryTable}
              alt={"Salary Table"}
            />
          </div>
        </div>
        <div className="flex flex-col h-full w-[20%]"></div>
      </div>
      */}

      <div className="w-screen">
        <div className="flex md:flex-row flex-col bg-[#3f4d62] text-white md:w-full w-full md:px-44">
          <div className="flex flex-col md:w-1/2 md:ml-64 md:mr-20 m-20">
            <span className="text-3xl text-[#f6e05e] font-bold mb-5">
              NEXT STEP:
            </span>
            <span className="text-4xl text-white-900 font-bold mb-5">
              Explore The Average Salary Of Your Preferred Candidate
            </span>
            <span className="mb-5 text-white-900">
              You have the Job Description. Now it's time to explore our
              comprehensive salary guide to start hiring your ideal candidate!
            </span>
            <div className="md:justify-start justify-center">
              <a
                href={buildMiiLinks.salaryGuide}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-500"
              >
                <button
                  className={
                    "w-[20rem] h-[4rem] bg-gradient-to-r from-themeColor-200 to-themeColor-100 text-white-900 text-2xl font-bold leading-10  rounded-full hover:bg-gray-300"
                  }
                >
                  Download the Guide
                </button>
              </a>
            </div>
          </div>
          <div className="flex md:w-1/2 items-end justify-center md:px-10 px-5">
            <img
              className="w-auto md:h-auto h-64"
              src={salaryTable}
              alt={"Salary Table"}
            />
          </div>
        </div>
      </div>

      {isBuildMiiEmailModalOpen && (
        <BuildMiiEmailModal
          value={isBuildMiiEmailModalOpen}
          onClick={handleBuildMiiEmailModalClick}
          jobDescription={jobDescription}
          role={jobTitle}
          uId={uId}
          emailStatus={emailSentObject.emailStatus}
          setEmailStatus={setEmailStatus}
        />
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          message="Are you sure you want to leave this page?"
          value={isConfirmationModalOpen}
          onClick={handleConfirmationModalClick}
        />
      )}
      {isBuildMiiRefetchModalOpen && (               
        <BuildMiiRefetchModal
          onClick={handleRefetchJD}
          jobDescriptionStatus={jobDescriptionStatus}
        />
      )}
      {emailSentObject.showEmailModal && (
        <BuildMiiEmailSentModal
          title={emailSentObject.emailTitle}
          message={emailSentObject.emailMessage}
          showEmailModal={emailSentObject.showEmailModal}
          onClick={handleBuildMiiEmailSentModalClick}
        />
      )}
      {warningObject.showWarningModal && (
        <BuildMiiWarningModal
          title={warningObject.warningTitle}
          message={warningObject.warningMessage}
          showWarningModal={warningObject.showWarningModal}
          onClick={handleWarningModalClose}
        />
      )}
      {loading && <Loader />}
    </>
  );
};
export default BuildMiiJd;
